import {
  AttachFile,
  Close,
  Delete,
  Publish,
  RemoveRedEye,
} from '@mui/icons-material'
import { Box, Button, IconButton, Modal, Typography } from '@mui/material'
import {
  ChangeEvent,
  ElementRef,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useDeleteFileMutation, useGetFileQuery } from 'services/file'
import axiosInstance from 'store/axiosInstance'
import { IFile } from 'types/file'
import { RequiredStar } from './RequiredStarSpan'
import { UseFormSetError } from 'react-hook-form'
import { fileData } from 'consts/fileData'
import { downloadBlobData, openBlobData } from 'utils/file'
import { findJavaScriptCodes } from 'utils/check-malicious-pdf-content'

interface Props {
  text?: string
  isMulti?: boolean
  isRequired?: boolean
  handleFile?: (file: IFile) => void
  handleAsRawFile?: (file: File) => void
  file?: IFile
  fileId?: string
  accept?: string
  sendToApi?: boolean
  onDelete?: (file?: IFile) => void
  isDisabled?: boolean
  sizeLimit?: number
  setError?: UseFormSetError<any>
  triggerDelete?: boolean
  disableUpload?: boolean
  disableDelete?: boolean
  ignoreApiFile?: boolean
  previewImage?: boolean
}

export const FileUpload: FunctionComponent<Props> = ({
  text,
  isMulti = false,
  isRequired = false,
  handleFile,
  handleAsRawFile,
  file: prefilledFile,
  sendToApi = true,
  onDelete,
  fileId,
  isDisabled = false,
  sizeLimit = null,
  setError,
  accept,
  triggerDelete,
  disableUpload,
  disableDelete,
  ignoreApiFile,
  previewImage = false,
}) => {
  const { t } = useTranslation()
  const uploadInputRef = useRef<ElementRef<'input'>>(null)
  const [file, setFile] = useState<IFile | undefined>(prefilledFile)
  const [files, setFiles] = useState<any>([])
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [previewImgSrc, setPreviewImgSrc] = useState<null | string>(null)
  const { data: fileFromApi } = useGetFileQuery(
    { id: fileId ?? 0 },
    { skip: !fileId }
  )

  const [deleteFile, { isError, isSuccess }] = useDeleteFileMutation()

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if ((e.target.files as any)[0].type === 'application/pdf') {
      const isJavascriptCodeExist = await findJavaScriptCodes(e.target.files![0])
      if (isJavascriptCodeExist.length > 0) {
        toast.error(t('malicious-content-detected'))
        return
      }
    }
    if (
      sizeLimit &&
      setError &&
      (e.target.files?.item(0)?.size as any) > sizeLimit
    ) {
      setError('attachmentFileId', {
        type: 'manual',
        message: t('max-file-size-10mb'),
      })
      return
    }
    if (handleAsRawFile) {
      if (!isMulti) {
        handleAsRawFile((e.target.files as any)[0])
      } else {
        const formData = new FormData()
        formData.append('files', e.target.files?.item(0) as any)
        setFiles(formData)
        handleAsRawFile(formData as any)
      }
    }

    if (!sendToApi && uploadInputRef.current) {
      uploadInputRef.current.value = ''
    }

    if (sendToApi) {
      const formData = new FormData()
      formData.append('files', e.target.files?.item(0) as any)
      axiosInstance
        .post<FormData, IFile[]>('/files/upload', formData)
        .then((res: IFile[]) => {
          if (!ignoreApiFile) {
            setFile(res[0])
          }
          handleFile && handleFile(res[0])
        })

      if (uploadInputRef.current) uploadInputRef.current.value = ''
    }
  }

  const handleDelete = () => {
    deleteFile({ id: file?.id as string })
    onDelete && onDelete(file)
  }

  useEffect(() => {
    if (isSuccess) {
      setFile(undefined)
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  useEffect(() => {
    if (fileFromApi) {
      setFile(fileFromApi)
    }
  }, [fileFromApi])

  useEffect(() => {
    if (triggerDelete) {
      setFile(undefined)
    }
  }, [triggerDelete])

  const handleDownload = () => {
    if (file) {
      axiosInstance
        .post<
          void,
          Blob
        >(`/files/${file.id}/download`, undefined, { responseType: 'blob' })
        .then((data) => {
          if (data.type === 'application/pdf') {
            openBlobData(data)
          } else {
            downloadBlobData(
              data,
              file.originalName ||
                (data?.type
                  ? `${file.name}.${fileData[data.type]?.extensions?.[0] || 'pdf'}`
                  : undefined)
            )
          }
        })
    }
  }

  function openImgBlobData(data: string | Blob) {
    const blob = new Blob([data])
    const imageUrl = window.URL.createObjectURL(blob)
    setPreviewImgSrc(imageUrl)
    setIsPreviewModalOpen(true)
  }

  const closeModal = () => {
    setIsPreviewModalOpen(false)
    setPreviewImgSrc(null)
  }

  const handleDownloadAndPreviewImg = () => {
    if (file) {
      axiosInstance
        .post<void, Blob>(`/files/${file.id}/download`, undefined, {
          responseType: 'blob',
        })
        .then((data) => {
          if (
            data.type === 'image/png' ||
            data.type === 'image/gif' ||
            data.type === 'image/jpeg'
          ) {
            openImgBlobData(data)
          } else {
            console.error('unexpected error is occured')
          }
        })
        .catch((error) => console.error('unexpected error is occured'))
    }
  }

  return (
    <Box display="flex" position="relative" alignItems="center" gap="5px">
      <input
        type="file"
        multiple={isMulti}
        hidden
        ref={uploadInputRef}
        onChange={handleChange}
        accept={accept}
        disabled={isDisabled || disableUpload}
      />
      {file && (
        <>
          <Box
            sx={(theme) => ({
              background: theme.palette.primary.main,
              padding: '5px',
              width: '28px',
              height: '100%',
              borderRadius: '4px',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer',
              marginLeft: '5px',
            })}
          >
            <AttachFile
              onClick={handleDownload}
              sx={{ color: 'white' }}
              fontSize="small"
            />
          </Box>
        </>
      )}
      {file && previewImage && (
        <>
          <Box
            sx={(theme) => ({
              background: theme.palette.primary.main,
              width: '28px',
              height: '100%',
              padding: '5px',
              borderRadius: '4px',
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer',
            })}
          >
            <RemoveRedEye
              onClick={handleDownloadAndPreviewImg}
              sx={{ color: 'white' }}
              fontSize="small"
            />
          </Box>
        </>
      )}
      <Button
        variant="text"
        startIcon={<Publish />}
        onClick={() => {
          if (!disableUpload) {
            uploadInputRef.current && uploadInputRef.current.click()
          }
        }}
        disabled={isDisabled}
      >
        {file ? (
          <Typography
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '90px',
            }}
          >
            {file.originalName}
          </Typography>
        ) : (
          <>
            {text}
            {isRequired && <RequiredStar>*</RequiredStar>}
          </>
        )}
      </Button>

      <Modal open={isPreviewModalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: 900,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={closeModal}
          >
            <Close />
          </IconButton>
          {previewImgSrc && (
            <img src={previewImgSrc} alt="Preview" style={{ width: '100%' }} />
          )}
        </Box>
      </Modal>

      {file && !isDisabled && !disableDelete && (
        <IconButton onClick={handleDelete}>
          <Delete color="error" />
        </IconButton>
      )}
    </Box>
  )
}
